/**
 * Mobile layout for the viewer: on phone-width screens the three desktop
 * surfaces (navigator panel / board / details sidebar) become full-screen
 * tabs behind a bottom tab bar. This hook owns the breakpoint subscription,
 * the active tab, AND the "flip to Details on selection" mechanics.
 *
 * Selecting something on the board or in the nav flips to the Details tab —
 * on a phone the details sidebar isn't visible alongside the board, so the
 * selection would otherwise appear to do nothing. The flip is DEBOUNCED past
 * the double-tap window: a double-tap's first tap selects, and flipping
 * immediately would hide the board before the second tap lands. An open/dive
 * (`showViewer` / a host-side cancel) or a deselect cancels the pending flip.
 *
 * The auto-flip watches the store's selection directly, so a board click —
 * which writes selection straight to the store, bypassing the nav's own flip —
 * still surfaces Details. Nav/sidebar paths call `scheduleDetailsFlip` /
 * `cancelDetailsFlip` explicitly.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  useWorkspaceSelection,
  type WorkspaceStore,
} from "../workbench/WorkspaceStore.js";

export type MobileTab = "files" | "viewer" | "details";

/** Matches Tailwind's `md` boundary: below it the side panels can't share
 *  the screen with the board, so the viewer switches to tabbed surfaces. */
const MOBILE_QUERY = "(max-width: 767px)";

export interface MobileLayoutController {
  /** Phone-width viewport — render the tabbed layout. Live (resize/rotate). */
  readonly isMobile: boolean;
  readonly mobileTab: MobileTab;
  readonly setMobileTab: (tab: MobileTab) => void;
  /** Debounced flip to the Details tab (past the double-tap window). */
  readonly scheduleDetailsFlip: () => void;
  /** Drop a pending flip (a dive/deselect asked NOT to surface Details). */
  readonly cancelDetailsFlip: () => void;
  /** Flip straight to the board, dropping any pending Details flip. */
  readonly showViewer: () => void;
}

export interface MobileLayoutOptions {
  /**
   * Whether the phone layout may engage at all. A native host sets this false:
   * there the breakpoint measures a PANE (a split editor is narrow without
   * being a phone), and the layout it would trigger is bottom-tab chrome the
   * host already provides. Default true — the open web.
   */
  readonly enabled?: boolean;
}

export function useMobileLayout(
  store: WorkspaceStore,
  { enabled = true }: MobileLayoutOptions = {},
): MobileLayoutController {
  const [narrow, setNarrow] = useState<boolean>(
    () =>
      typeof window !== "undefined" &&
      (window.matchMedia?.(MOBILE_QUERY).matches ?? false),
  );
  useEffect(() => {
    // jsdom has no matchMedia — the viewer then just stays desktop.
    const mq = window.matchMedia?.(MOBILE_QUERY);
    if (!mq) return;
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // Keep subscribing either way: a hook that conditionally listens would change
  // hook order, and the subscription costs nothing when the answer is ignored.
  const isMobile = enabled && narrow;

  const [mobileTab, setMobileTab] = useState<MobileTab>("viewer");

  const detailsFlipTimer = useRef<number | null>(null);
  const cancelDetailsFlip = useCallback(() => {
    if (detailsFlipTimer.current !== null) {
      clearTimeout(detailsFlipTimer.current);
      detailsFlipTimer.current = null;
    }
  }, []);
  const scheduleDetailsFlip = useCallback(() => {
    cancelDetailsFlip();
    detailsFlipTimer.current = window.setTimeout(() => {
      detailsFlipTimer.current = null;
      setMobileTab("details");
    }, 350);
  }, [cancelDetailsFlip]);
  const showViewer = useCallback(() => {
    cancelDetailsFlip();
    setMobileTab("viewer");
  }, [cancelDetailsFlip]);

  // Surface Details whenever a module/edge becomes selected from ANY source
  // (board clicks write straight to the store). Idempotent with the nav's flip.
  const { moduleId, edgeId } = useWorkspaceSelection(store);
  useEffect(() => {
    if (!isMobile) return;
    if (moduleId || edgeId) scheduleDetailsFlip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, moduleId, edgeId]);

  return {
    isMobile,
    mobileTab,
    setMobileTab,
    scheduleDetailsFlip,
    cancelDetailsFlip,
    showViewer,
  };
}
